import { useState, useRef, useEffect } from "react";
import { ZoomIn, ZoomOut, Move, Check, X } from "lucide-react";

interface ImageCropperProps {
  imageFile: File;
  onCropComplete: (croppedBlob: Blob) => void;
  onCancel: () => void;
  aspectRatio?: number;
}

export default function ImageCropper({
  imageFile,
  onCropComplete,
  onCancel,
  aspectRatio = 16 / 9,
}: ImageCropperProps) {
  const [imageSrc, setImageSrc] = useState<string>("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [cropping, setCropping] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      console.log("Image loaded from file, length:", result?.length);
      setImageSrc(result);
    };
    reader.onerror = (error) => {
      console.error("FileReader error:", error);
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleCrop = async () => {
    console.log("🔧 [IMAGE CROPPER v3.0 - FEB 11 2026 22:40] handleCrop called");
    console.log("handleCrop called", {
      hasCanvas: !!canvasRef.current,
      hasImage: !!imageRef.current,
      hasContainer: !!containerRef.current,
      imageLoaded,
      imageSrc: !!imageSrc
    });

    if (!canvasRef.current || !imageRef.current || !containerRef.current) {
      console.error("Cannot crop: missing refs");
      alert("เกิดข้อผิดพลาด: ไม่สามารถโหลดรูปภาพได้");
      return;
    }

    if (!imageRef.current.complete || !imageRef.current.naturalWidth) {
      console.error("Image not fully loaded");
      alert("กรุณารอสักครู่ รูปภาพยังโหลดไม่เสร็จ");
      return;
    }

    setCropping(true);

    const canvas = canvasRef.current;
    const image = imageRef.current;
    const container = containerRef.current;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    console.log("🔧 [IMAGE CROPPER v5.0 - FEB 13 2026 16:11] Starting crop process");
    console.log("📐 Container:", { containerWidth, containerHeight });
    console.log("🖼️ Image natural:", { width: image.naturalWidth, height: image.naturalHeight });
    console.log("🎯 State:", { scale, positionX: position.x, positionY: position.y });
    console.log("📏 Aspect Ratio:", aspectRatio);

    // Set output dimensions based on aspect ratio
    let targetWidth: number;
    let targetHeight: number;
    
    if (aspectRatio === 1) {
      // Profile image: 500×500px (1:1)
      targetWidth = 500;
      targetHeight = 500;
    } else if (aspectRatio === 16 / 5) {
      // Cover image: 1600×500px (16:5)
      targetWidth = 1600;
      targetHeight = 500;
    } else if (aspectRatio === 4 / 5) {
      // Intro image: 800×1000px (4:5)
      targetWidth = 800;
      targetHeight = 1000;
    } else if (aspectRatio === 16 / 9) {
      // Course thumbnail: 1280×720px (16:9)
      targetWidth = 1280;
      targetHeight = 720;
    } else {
      // Default: use high resolution
      targetWidth = Math.max(2400, image.naturalWidth);
      targetHeight = targetWidth / aspectRatio;
    }
    
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    console.log("📊 Canvas output:", { width: canvas.width, height: canvas.height, aspectRatio });

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // CSS: top:50%, left:50%, transform: translate(calc(-50% + position.x), calc(-50% + position.y)) scale(scale)
    // With transform-origin: center
    //
    // Breaking down the transform:
    // 1. Element positioned at top:50%, left:50% = element's top-left at container center
    // 2. translate(-50%, -50%) = move element so ITS center is at container center
    // 3. translate(position.x, position.y) = offset from center
    // 4. scale(scale) = scale around element center
    //
    // Final position of image center in container: (containerWidth/2 + position.x, containerHeight/2 + position.y)
    
    const natW = image.naturalWidth;
    const natH = image.naturalHeight;
    
    // Get actual displayed size from DOM (after CSS auto-sizing)
    const imageRect = image.getBoundingClientRect();
    const containerRectForCalc = container.getBoundingClientRect();
    
    // Base displayed size (before user's scale transform)
    const baseDispW = imageRect.width / scale;
    const baseDispH = imageRect.height / scale;
    
    // Displayed size after user's scaling
    const dispW = baseDispW * scale;
    const dispH = baseDispH * scale;
    
    // Image center position in container coordinates
    const centerX = containerWidth / 2 + position.x;
    const centerY = containerHeight / 2 + position.y;
    
    // Top-left corner of displayed image in container coordinates
    const imgX = centerX - dispW / 2;
    const imgY = centerY - dispH / 2;
    
    console.log("📍 Display:", { baseDispW, baseDispH, dispW, dispH, centerX, centerY, imgX, imgY });
    
    // Map container area to source image coordinates
    // Container covers (0, 0) to (containerWidth, containerHeight)
    // We need to find what part of the source image corresponds to this container area
    
    // Container corners in displayed image coordinates
    const containerLeft = 0;
    const containerTop = 0;
    const containerRight = containerWidth;
    const containerBottom = containerHeight;
    
    // Convert container corners to displayed image coordinates (relative to image position)
    const imgContainerLeft = containerLeft - imgX;
    const imgContainerTop = containerTop - imgY;
    const imgContainerRight = containerRight - imgX;
    const imgContainerBottom = containerBottom - imgY;
    
    // Convert to natural image coordinates
    // Ratio between natural size and displayed size
    const scaleToNatural = natW / baseDispW;
    const srcX = (imgContainerLeft / scale) * scaleToNatural;
    const srcY = (imgContainerTop / scale) * scaleToNatural;
    const srcW = ((imgContainerRight - imgContainerLeft) / scale) * scaleToNatural;
    const srcH = ((imgContainerBottom - imgContainerTop) / scale) * scaleToNatural;
    
    console.log("🔄 Scale to natural:", { scaleToNatural, natW, baseDispW });
    console.log("✂️ Container mapping:", { imgContainerLeft, imgContainerTop, imgContainerRight, imgContainerBottom });
    console.log("✂️ Source (natural):", { srcX, srcY, srcW, srcH });
    
    // Clamp to natural image bounds (safety)
    const clampedSourceX = Math.max(0, Math.min(srcX, natW));
    const clampedSourceY = Math.max(0, Math.min(srcY, natH));
    const clampedSourceWidth = Math.min(srcW, natW - clampedSourceX);
    const clampedSourceHeight = Math.min(srcH, natH - clampedSourceY);
    
    console.log("✅ Final:", { 
      x: clampedSourceX, 
      y: clampedSourceY, 
      w: clampedSourceWidth, 
      h: clampedSourceHeight 
    });

    // Draw the cropped portion to canvas
    ctx.drawImage(
      image,
      clampedSourceX,
      clampedSourceY,
      clampedSourceWidth,
      clampedSourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Use setTimeout to ensure canvas is fully rendered
    setTimeout(() => {
      // Use PNG for lossless quality, or high-quality JPEG
      canvas.toBlob(
        (blob) => {
          setCropping(false);
          if (blob) {
            console.log("Blob created successfully", blob.size);
            onCropComplete(blob);
          } else {
            console.error("Failed to create blob, trying fallback method");
            // Fallback: convert canvas to dataURL then to blob
            try {
              const dataUrl = canvas.toDataURL("image/jpeg", 0.98);
              fetch(dataUrl)
                .then(res => res.blob())
                .then(fallbackBlob => {
                  console.log("Fallback blob created", fallbackBlob.size);
                  onCropComplete(fallbackBlob);
                })
                .catch(err => {
                  console.error("Fallback also failed", err);
                  alert("เกิดข้อผิดพลาดในการประมวมผลรูปภาพ");
                  setCropping(false);
                });
            } catch (err) {
              console.error("Error in fallback", err);
              alert("เกิดข้อผิดพลาดในการประมวมผลรูปภาพ");
              setCropping(false);
            }
          }
        },
        "image/jpeg",
        0.98
      );
    }, 100);
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-xl w-full p-3 my-auto max-h-[95vh] overflow-y-auto">
        <div className="mb-2">
          <h2 className="text-lg font-bold text-gray-800 mb-1">
            ปรับตำแหน่งรูปภาพ
          </h2>
          <p className="text-xs text-gray-600">
            ลากเพื่อเลื่อนตำแหน่ง และใช้ปุ่มซูมเพื่อปรับขนาด
          </p>
        </div>

        <div
          ref={containerRef}
          className="relative bg-gray-900 rounded-lg overflow-hidden mb-2 mx-auto"
          style={{
            width: aspectRatio === 1 ? "200px" : aspectRatio === 16/5 ? "100%" : aspectRatio === 4/5 ? "300px" : aspectRatio < 1 ? `${aspectRatio * 60}vh` : "100%",
            maxWidth: aspectRatio === 1 ? "200px" : aspectRatio === 16/5 ? "600px" : aspectRatio === 4/5 ? "300px" : aspectRatio < 1 ? "300px" : "100%",
            height: aspectRatio === 1 ? "200px" : aspectRatio === 16/5 ? "auto" : aspectRatio === 4/5 ? "375px" : aspectRatio < 1 ? "60vh" : "auto",
            paddingBottom: aspectRatio === 16/5 ? "31.25%" : aspectRatio > 1 && aspectRatio !== 16/5 ? `${Math.min((1 / aspectRatio) * 100, 50)}%` : "0",
            maxHeight: aspectRatio === 16/5 ? "200px" : "none",
            aspectRatio: aspectRatio === 1 ? "1 / 1" : aspectRatio === 4/5 ? "4 / 5" : "auto",
            cursor: isDragging ? "grabbing" : "grab",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="absolute inset-0 overflow-hidden">
            {imageSrc && (
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Crop preview"
                className="select-none absolute"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${scale})`,
                  transformOrigin: "center",
                }}
                draggable={false}
                onLoad={(e) => {
                  console.log("Image onLoad fired", {
                    naturalWidth: (e.target as HTMLImageElement).naturalWidth,
                    naturalHeight: (e.target as HTMLImageElement).naturalHeight
                  });
                  setImageLoaded(true);
                }}
                onError={(e) => {
                  console.error("Failed to load image", e);
                  setImageLoaded(false);
                }}
              />
            )}
            {!imageLoaded && imageSrc && (
              <div className="text-white text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-2"></div>
                <p>กำลังโหลดรูปภาพ...</p>
              </div>
            )}
          </div>

          <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-lg text-sm flex items-center gap-2">
            <Move className="w-4 h-4" />
            ลากเพื่อเลื่อน
          </div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-lg transition-colors"
              disabled={scale <= 0.5}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <div className="text-xs font-semibold text-gray-700 min-w-[50px] text-center">
              {Math.round(scale * 100)}%
            </div>
            <button
              onClick={handleZoomIn}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-lg transition-colors"
              disabled={scale >= 3}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
              ยกเลิก
            </button>
            <button
              onClick={handleCrop}
              disabled={cropping || !imageSrc}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-pink-600 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title={!imageSrc ? "กรุณารอรูปภาพโหลด" : cropping ? "กำลังประมวลผล" : "คลิกเพื่อใช้รูปนี้"}
            >
              {cropping ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  กำลังประมวลผล...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  ใช้รูปนี้
                </>
              )}
            </button>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}
